import React from 'react';
import ReactDOM from 'react-dom';

import Getters from 'data/getters';
import Reactor from 'data/reactor';
import Actions from 'data/actions';
import Popover from 'utils/popover';
import Analytics from 'utils/analytics';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

export default CreateReactClass({
    displayName: 'HelpBox',
    propTypes: {
        helpContentsRenderer: PropTypes.func.isRequired,
        id: PropTypes.string.isRequired, // Id here, must be unique. Append studentBid to text to make up the unique id.
        appLevelCssClassName: PropTypes.string, // Pass any CSS class names that should be added/removed from the #app-content element to help with styling.
        moduleName: PropTypes.string, // used for google analytics label
        helpLabel: PropTypes.string, // used for google analytics label
        adjustOverflow: PropTypes.bool, // If true, moves alignment more to left so that it doesn't overflow on the right of the screen. Consider using horizontalPlacementIsDynamic.
        additionalOffset: PropTypes.bool, //Additional offset for text boxes that need heavy offsetting
        optionalText: PropTypes.string, //If supplied will show optional text next to the help icon, clicking the text will open the help same as the help icon
        optionalImage: PropTypes.string, //If supplied will show optional image tag next to the help icon, clicking the image will open the help same as the help icon
        renderIcon: PropTypes.bool, //If supplied and false the question mark icon will not be rendered
        renderIconClassName: PropTypes.string, // If supplied will change the className of the help icon from default
        isHelpBoxOpen: PropTypes.func, // Informs parent components of the state of this helpbox
        placement: PropTypes.oneOf(['top', 'bottom']), // Override for if the help box will pop above or below the help icon
        horizontalPlacementIsDynamic: PropTypes.bool, // Set to true if the location of the help icon is dynamic. Will make the component set the help box to left or right depending on location on viewport. See help.less values for `.helpbox-{right/left} .help-box-content`. This may conflict with the adjustOverflow prop that sets a static 'align-left' or 'align-right'.
        testid: PropTypes.string, // Optional - add a specific data-testid to help box
    },

    getBaseClassName() {
        return this.props.optionalImage ? 'help-box help-image' : 'help-box';
    },

    getDefaultProps() {
        return {
            appLevelCssClassName: '',
            adjustOverflow: false,
            renderIcon: true,
            renderIconClassName: 'icon icon-help-tutorial',
        };
    },

    getInitialState() {
        return {
            expandedClass: 'hide-fade',
            isExpanded: false,
        };
    },

    componentDidMount() {
        document.addEventListener('keydown', this.handleEscKey, false);
        document.addEventListener('click', this.handleClickAnywhere, false);
    },

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleEscKey, false);
        document.removeEventListener('click', this.handleClickAnywhere, false);
    },

    toggleHelpBox(event) {
        Popover.closeOpenPopovers('HelpBox');
        Popover.closeChartPopoverBoxes();
        const helpLabel = this.props.helpLabel ? this.props.helpLabel : '';

        if (this.props.moduleName) {
            Analytics.recordButtonClick(this.props.moduleName, 'help-call-out', helpLabel);
        }
        if (this.state.isExpanded) {
            this.closePopover(event);
        } else {
            this.setState({
                expandedClass: 'show-fade jump-up-z-index',
                isExpanded: true,
            });
            const helpBoxContainer = event.target.parentElement;
            helpBoxContainer.className = this.getBaseClassName() + ' ' + this.determinePlacement();
            event.stopPropagation();
        }
    },

    determinePlacement() {
        const helpIconNode = ReactDOM.findDOMNode(this.refs['help-icon-' + this.props.id]);

        if (this.props.placement && !this.props.horizontalPlacementIsDynamic) {
            return this.props.placement;
        } else if (this.props.horizontalPlacementIsDynamic) {
            const horizontalPosition = helpIconNode.getBoundingClientRect().x;
            const halfwayAcrossScreenCoordinate = (window.innerWidth || document.documentElement.clientWidth) / 2;

            let offsetDirection = null;
            if (horizontalPosition >= halfwayAcrossScreenCoordinate) {
                offsetDirection = 'helpbox-left';
            } else {
                offsetDirection = 'helpbox-right';
            }

            return `${this.props.placement} ${offsetDirection}`;
        }

        // this code implements auto placement with the following caveats:
        //      1. auto only chooses between top and bottom, it will not pick left/right
        //      2. it uses top when possible
        //      3. it is hardcoded to check 200 pixels from the top of the screen.  If your
        //              popup is larger than 200, top may still be chosen.
        let position = 'top';

        if (helpIconNode) {
            const helpIconRect = helpIconNode.getBoundingClientRect();
            const helpBoxNode = ReactDOM.findDOMNode(this.refs['help-box-popover-' + this.props.id]);
            const helpBoxRect = helpBoxNode.getBoundingClientRect();
            if (helpIconRect) {
                if (helpIconRect.top < helpBoxRect.height) {
                    position = 'bottom';
                }
            }
        }

        return position;
    },

    closePopover(event) {
        if (!this.state.isExpanded) {
            return;
        }
        // For the print and share modal. It sends the modal its current
        // expanded state before isExpanded is set to false. This lets the
        // modal know that the help box was open at the time of pressing esc.
        if (this.props.isHelpBoxOpen && event.type != 'click') {
            this.props.isHelpBoxOpen(this.state.isExpanded);
        }
        this.setState({
            expandedClass: 'hide-fade',
            isExpanded: false,
        });
        event.stopPropagation();
    },

    handleEscKey: function(event) {
        if (event.key === 'Escape' || event.keyCode == 27) {
            this.closePopover(event);
        }
    },

    // The following function searches for the closest ancestor element that contains those particular ids.
    // If it does find them, it does not run the closePopOver function.
    handleClickAnywhere(event) {
        const isNotHelpIcon = !event.target.closest('#help-icon-' + this.props.id);
        const isNotInsideHelpBox = !event.target.closest('#help-box-popover-' + this.props.id);
        const isNotOptionalText = event.target.id !== 'help-box-optional-text-' + this.props.id;
        const isNotOptionalImg = !event.target.classList.contains('help-box-optional-image');
        if (isNotHelpIcon && isNotInsideHelpBox && isNotOptionalText && isNotOptionalImg) {
            this.closePopover(event);
        }
    },

    handleClickWithinHelpBox(event) {
        event.stopPropagation();
    },

    renderOptionalText() {
        if (this.props.optionalText) {
            return (
                <span
                    id={`help-box-optional-text-${this.props.id}`}
                    className="help-box-optional-text"
                    onClick={this.toggleHelpBox}
                >
                    {this.props.optionalText}
                    &#9662;
                </span>
            );
        }
        return null;
    },

    renderOptionalImage() {
        if (this.props.optionalImage) {
            let elements = [];
            elements.push(
                <img
                    key="help-box-optional-image"
                    className="help-box-optional-image"
                    src={this.props.optionalImage}
                    onClick={this.toggleHelpBox}
                />
            );
            if (!this.props.optionalText) {
                elements.push(
                    <span
                        key="help-box-optional-image-tick"
                        className="help-box-optional-image-tick"
                        onClick={this.toggleHelpBox}
                    >
                        &#9662;
                    </span>
                );
            }
            return elements;
        }
        return null;
    },

    renderHelpIcon() {
        if (this.props.renderIcon) {
            return (
                <span
                    ref={`help-icon-${this.props.id}`}
                    id={`help-icon-${this.props.id}`}
                    key={`help-icon-${this.props.id}`}
                    className={this.props.renderIconClassName}
                    onClick={this.toggleHelpBox}
                    data-testid={this.props.testid}
                />
            );
        } else {
            return null;
        }
    },

    render() {
        const alignment = this.props.adjustOverflow ? 'align-left' : 'align-right';
        const additionalOffset = this.props.additionalOffset ? 'additionalHelpBoxOffset' : null;
        return (
            <div className={this.getBaseClassName()}>
                {this.renderHelpIcon()}
                <div
                    className={`${this.state.expandedClass} arrow back`}
                    onClick={this.handleClickWithinHelpBox}
                    key={`help-arrow-back-${this.props.id}`}
                />
                <div
                    className={`${this.state.expandedClass} ${additionalOffset} help-box-content ${alignment}`}
                    id={`help-box-popover-${this.props.id}`}
                    key={`help-box-content-${this.props.id}`}
                    ref={`help-box-popover-${this.props.id}`}
                    onClick={this.handleClickWithinHelpBox}
                    data-testid={`${this.props.testid}-container`}
                >
                    <span
                        className="close-btn text-uppercase"
                        onClick={this.closePopover}
                        key={`help-close-button-${this.props.id}`}
                    >
                        ✕
                    </span>
                    {this.props.helpContentsRenderer(this.toggleHelpBox)}
                </div>
                <div
                    className={`${this.state.expandedClass} arrow front `}
                    key={`help-arrow-front-${this.props.id}`}
                    onClick={this.handleClickWithinHelpBox}
                />
                {this.renderOptionalImage()}
                {this.renderOptionalText()}
            </div>
        );
    },
});
